import { gql, useSuspenseQuery } from '@apollo/client'
import { getApolloClient } from '../../client/apollo/apolloClientSingleton'
import cache from '../../client/apollo/cache'

const CURRENT_USER = gql`
  query currentUser {
    currentUser {
      objectId
      username
      email
      address
      userAbTestFraction
      saleConfig
      businessBuyerConfig
      locale
      createdAt
      p2pOptOut
    }
  }
`

export const getCurrentCachedUser = () => {
  const data = getApolloClient().readQuery({ query: CURRENT_USER })
  return data.currentUser
}

export const getCurrentUser = async () => {
  const { data } = await getApolloClient().query({
    query: CURRENT_USER
  })
  return data.currentUser
}

export const useCurrentUser = () => {
  const { data } = useSuspenseQuery(CURRENT_USER, {})
  return data.currentUser
}

export const refetchCurrentUser = () =>
  getApolloClient().query({ query: CURRENT_USER, fetchPolicy: 'network-only' })

export const resetCurrentUserCache = () =>
  cache.writeQuery({
    query: CURRENT_USER,
    data: { currentUser: null },
    broadcast: true
  })

/**
 * Listen for the user becoming logged in to defer an action until they are.
 * Prefer other methods when possible!
 * @param {(user: any) => void} callback
 */
export const onUserLoggedIn = (callback) => {
  const observable = getApolloClient().watchQuery({
    query: CURRENT_USER,
    fetchPolicy: 'cache-only'
  })
  const subscription = observable.subscribe({
    next: (value) => {
      const currentUser = value.data.currentUser
      if (currentUser) {
        callback(currentUser)
        subscription.unsubscribe()
      }
    }
  })
}

/**
 * Cache the current parse user from local storage for quicker page load
 * This should only be called when initializing the app
 *
 * @param {Parse.User} [user]
 */
export const _precacheLocalStorageCurrentUser = async (user) => {
  cache.writeQuery({
    query: CURRENT_USER,
    data: {
      currentUser: user
        ? {
            __typename: 'User',
            objectId: user.id,
            username: user.get('username'),
            email: user.get('email') ?? null,
            address: user.get('address') ?? null,
            userAbTestFraction: user.get('userAbTestFraction'),
            saleConfig: user.get('saleConfig') ?? null,
            businessBuyerConfig: user.get('businessBuyerConfig') ?? null,
            locale: user.get('locale'),
            createdAt: user.createdAt.toISOString(),
            p2pOptOut: user.get('p2pOptOut') ?? false
          }
        : null
    }
  })

  // Cached user might have change since we cached it
  if (user) await refetchCurrentUser()
}
